import React from 'react';
import PropTypes from 'prop-types';
import { incrementSubVersion, incrementMajorVersion } from '../../utils/versionUtils';
import './VersionModal.css';
import { readSnapshot, hasSnapshot } from '../../utils/versionHistory';

const VersionModal = ({ isOpen, onClose, currentVersion, currentAuthor, loggedInAuthor, versionHistory, onNewVersion, onViewVersion, onRollback, readOnly }) => {
  const defaultAuthor = loggedInAuthor || 'Unbekannt';
  const [author, setAuthor] = React.useState(currentAuthor || defaultAuthor);
  const [comment, setComment] = React.useState('');
  const suggestedSub = incrementSubVersion(currentVersion || '1.0');
  const suggestedMajor = incrementMajorVersion(currentVersion || '1.0');

  // Sync author when modal opens with new data
  React.useEffect(() => {
    setAuthor(currentAuthor || defaultAuthor);
  }, [currentAuthor, isOpen, defaultAuthor]);

  // Reset comment when modal opens
  React.useEffect(() => {
    if (isOpen) setComment('');
  }, [isOpen]);

  if (!isOpen) return null;

  const handleNewVersion = (newVersion) => {
    const authorName = author.trim() || defaultAuthor;
    onNewVersion(newVersion, authorName, comment.trim());
    setComment('');
  };

  const handleViewClick = (entry) => {
    const xml = readSnapshot(entry);
    if (xml && onViewVersion) {
      onViewVersion(xml, entry.version, entry.images);
      onClose();
    }
  };

  const handleRollbackClick = (entry) => {
    if (onRollback) {
      onRollback(entry);
      onClose();
    }
  };

  const history = Array.isArray(versionHistory) ? versionHistory : [];

  return (
    <div className="version-modal-overlay">
      <button type="button" className="version-modal-overlay-btn" onClick={onClose} aria-label="Dialog schließen" />
      <dialog className="version-modal" open aria-label="Versionsverwaltung">
        <div className="version-modal-header">
          <h5><i className="bi bi-tag"></i> Versionsverwaltung</h5>
          <button className="version-modal-close" onClick={onClose}>&times;</button>
        </div>
        <div className="version-modal-body">
          {/* Current version info */}
          <div className="version-info-section">
            <div className="version-current">
              <span className="version-label">Aktuelle Version</span>
              <span className="version-number">v{currentVersion || '1.0'}</span>
            </div>
            <div className="version-field">
              <label htmlFor="version-author">Autor</label>
              <input
                id="version-author"
                type="text"
                value={author}
                onChange={(e) => setAuthor(e.target.value)}
                placeholder="Name des Autors"
                disabled={readOnly}
              />
            </div>
          </div>

          {/* New version buttons */}
          {!readOnly && (
            <div className="version-actions">
              <div className="version-comment-field">
                <label htmlFor="version-comment">Kommentar zur neuen Version</label>
                <textarea
                  id="version-comment"
                  className="version-comment-input"
                  value={comment}
                  onChange={(e) => setComment(e.target.value)}
                  placeholder="Änderungen beschreiben (optional)…"
                  rows={2}
                  maxLength={500}
                />
              </div>
              <span className="version-actions-label">Neue Version erstellen:</span>
              <div className="version-buttons">
                <button
                  className="btn btn-outline-primary btn-sm version-btn"
                  onClick={() => handleNewVersion(suggestedSub)}
                  title={`Sub-Version: ${currentVersion} → ${suggestedSub}`}
                >
                  <i className="bi bi-plus-circle"></i> Sub-Version <strong>{suggestedSub}</strong>
                </button>
                <button
                  className="btn btn-outline-danger btn-sm version-btn"
                  onClick={() => handleNewVersion(suggestedMajor)}
                  title={`Haupt-Version: ${currentVersion} → ${suggestedMajor}`}
                >
                  <i className="bi bi-arrow-up-circle"></i> Haupt-Version <strong>{suggestedMajor}</strong>
                </button>
              </div>
            </div>
          )}

          {/* Version history */}
          <div className="version-history-section">
            <h6><i className="bi bi-clock-history"></i> Bearbeitungshistorie</h6>
            {history.length === 0 ? (
              <p className="version-history-empty">Keine Einträge vorhanden.</p>
            ) : (
              <div className="version-history-scroll">
                <table className="version-history-table">
                  <thead>
                    <tr>
                      <th>Version</th>
                      <th>Bearbeitet von</th>
                      <th>Kommentar</th>
                      <th>Datum</th>
                      <th style={{ width: readOnly ? '50px' : '90px' }}></th>
                    </tr>
                  </thead>
                  <tbody>
                    {[...history].reverse().map((entry, idx) => (
                      <tr key={`${entry.version}-${entry.date}-${idx}`} className={idx === 0 ? 'version-history-current' : ''}>
                        <td><strong>v{entry.version}</strong></td>
                        <td>{entry.author || '—'}</td>
                        <td className="version-comment-cell">{entry.comment || '—'}</td>
                        <td>{entry.date || '—'}</td>
                        <td className="version-actions-cell">
                          {hasSnapshot(entry) && readOnly && (
                            <button
                              type="button"
                              className="version-view-btn"
                              onClick={() => handleViewClick(entry)}
                              title={`Version ${entry.version} anzeigen`}
                            >
                              <i className="bi bi-eye"></i>
                            </button>
                          )}
                          {!readOnly && hasSnapshot(entry) && (
                            <button
                              type="button"
                              className="version-rollback-btn"
                              onClick={() => handleRollbackClick(entry)}
                              title={`Auf Version ${entry.version} zurückrollen`}
                            >
                              <i className="bi bi-arrow-counterclockwise"></i>
                            </button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      </dialog>
    </div>
  );
};

VersionModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  currentVersion: PropTypes.string.isRequired,
  currentAuthor: PropTypes.string.isRequired,
  loggedInAuthor: PropTypes.string,
  versionHistory: PropTypes.arrayOf(
    PropTypes.shape({
      version: PropTypes.string,
      author: PropTypes.string,
      date: PropTypes.string,
      comment: PropTypes.string,
      xml: PropTypes.string,
    })
  ),
  onNewVersion: PropTypes.func.isRequired,
  onViewVersion: PropTypes.func,
  onRollback: PropTypes.func,
  readOnly: PropTypes.bool,
};

export default VersionModal;
